import { css, SerializedStyles } from '@emotion/react';
import { BREAK_POINT_TABLE, TABLET_CLAMP_BASIS_WIDTH, MOBILE_CLAMP_BASIS_WIDTH, MINIMUM_MOBILE_WIDTH, MOBILE_MAX_WIDTH, TABLET_MAX_WIDTH, DESIGN_MOBILE_WIDTH } from '../consts';

/**
 * css関数のclamp()を適用する汎用処理
 * @param {String} minValue 指定したい最小値（単位付き）
 * @param {String} maxValue 指定したい最大値（単位付き）
 * @param {Number} customBasisWidth デフォルトではiPhone 5/SEを基準に320を指定、カスタムで指定がある場合は値を入れる
 * @param {String} criterionUnit デフォルトではvwの単位を指定、もし違う単位を使う場合は値を入れる
 */
type getCssClampArgsType = {
  minValue: string
  maxValue: string
  customBasisWidth?: number
  criterionUnit?: string
}
const transformNumberForGetCssClamp = (text: string): number => Number(text.replace(new RegExp(`${['px', '%', 'em', 'rem', 'pt'].join('|')}`, 'g'), ''));
export const getCssClamp = ({ minValue, maxValue, customBasisWidth = MOBILE_CLAMP_BASIS_WIDTH, criterionUnit = 'vw' }: getCssClampArgsType) => {
  return `clamp(${minValue}, ${(transformNumberForGetCssClamp(minValue) / customBasisWidth) * 100}${criterionUnit}, ${maxValue})`;
};

/**
 * emotion用にメディアクエリを適用する汎用処理
 */
type ClampType = (minValue: string, maxValue: string) => string;
type SetAutomaticDesignBasedSizeType = (pcDesignSize: number, mobileDesignSize: number, criterionUnit?: string) => string;
type StylesType = (args: { clamp: ClampType, setAutomaticDesignBasedSize?: SetAutomaticDesignBasedSizeType }) => SerializedStyles;
const getCustomBasisWidth = (customBreakPoint: number): number => {
  if (customBreakPoint > TABLET_CLAMP_BASIS_WIDTH) return TABLET_CLAMP_BASIS_WIDTH;
  return MOBILE_CLAMP_BASIS_WIDTH;
};
export const mediaQueries = () => {
  return {
    tablet: (styles: StylesType) => {
      const breakPoint = BREAK_POINT_TABLE['tablet'];
      const clamp = (minValue: string, maxValue: string) => getCssClamp({ minValue, maxValue, customBasisWidth: TABLET_CLAMP_BASIS_WIDTH });

      // pcのデザインサイズを基準に自動でclampを設定する処理
      // pcの90%を最大サイズとして計算
      // NOTE: 機械的な処理のため万能ではないです
      const setAutomaticDesignBasedSize = (pcDesignSize: number, mobileDesignSize: number, criterionUnit = 'px') => {
        const minSize = Math.ceil(mobileDesignSize * ((MOBILE_MAX_WIDTH + (TABLET_MAX_WIDTH - MOBILE_MAX_WIDTH)) / DESIGN_MOBILE_WIDTH));
        const maxSize = pcDesignSize;

        // 計算の結果、同値または最小値の方が大きかった場合は設定しない
        if (minSize === maxSize || minSize > maxSize || minSize >= pcDesignSize) return '';

        const minValue = `${minSize}${criterionUnit}`;
        const maxValue = `${maxSize}${criterionUnit}`;

        return getCssClamp({ minValue, maxValue, customBasisWidth: TABLET_CLAMP_BASIS_WIDTH });
      };

      return css`
        @media (max-width: ${breakPoint}) {
          ${styles({ clamp, setAutomaticDesignBasedSize })}
        }
      `;
    },
    mobile: (styles: StylesType) => {
      const breakPoint = BREAK_POINT_TABLE['mobile'];
      const clamp = (minValue: string, maxValue: string) => getCssClamp({ minValue, maxValue, customBasisWidth: MOBILE_CLAMP_BASIS_WIDTH });

      // mobileのデザインサイズを基準に自動でclampを設定する処理
      // NOTE: 機械的な処理のため万能ではないです
      const setAutomaticDesignBasedSize = (pcDesignSize: number, mobileDesignSize: number, criterionUnit = 'px') => {
        const minSize = Math.ceil(mobileDesignSize * (MINIMUM_MOBILE_WIDTH / DESIGN_MOBILE_WIDTH));

        const maxCalculatedSize = Math.ceil(mobileDesignSize * (MOBILE_MAX_WIDTH / DESIGN_MOBILE_WIDTH));
        const maxSize = maxCalculatedSize > pcDesignSize ? pcDesignSize : maxCalculatedSize;

        // 計算の結果、同値または最小値の方が大きかった場合は設定しない
        if (minSize === maxSize || minSize > maxSize || minSize >= pcDesignSize) return '';

        const minValue = `${minSize}${criterionUnit}`;
        const maxValue = `${maxSize}${criterionUnit}`;

        return getCssClamp({ minValue, maxValue, customBasisWidth: MOBILE_CLAMP_BASIS_WIDTH });
      };

      return css`
        @media (max-width: ${breakPoint}) {
          ${styles({ clamp, setAutomaticDesignBasedSize })}
        }
      `;
    },
    custom: (styles: StylesType, customBreakPoint?: number) => {
      const basisWidth = getCustomBasisWidth(customBreakPoint);
      const clamp = (minValue: string, maxValue: string) => getCssClamp({ minValue, maxValue, customBasisWidth: basisWidth });

      return css`
        @media (max-width: ${customBreakPoint}px) {
          ${styles({ clamp })}
        }
      `;
    },
  };
};
export const autoResponsiveClamp = (propertyName: string, pcValue: number, smartphoneValue: number, valueUnit = 'px'): SerializedStyles => {
  const { tablet, mobile } = mediaQueries();

  return css`
    ${tablet(({ setAutomaticDesignBasedSize }) => css`
      ${propertyName}: ${setAutomaticDesignBasedSize(pcValue, smartphoneValue, valueUnit)};
    `)}

    ${mobile(({ setAutomaticDesignBasedSize }) => css`
      ${propertyName}: ${setAutomaticDesignBasedSize(pcValue, smartphoneValue, valueUnit)};
    `)}
  `;
};
